import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon } from '@heroicons/react/24/outline'
import { suggestKits } from '../../api/kits'
import { inverterManufacturers, magazineManufacturers } from '../../filtersSchema'
import useTeczka from '../../hooks/useTeczka'
import { sprzetZWariantu } from '../../lib/teczka/sprzet'
import { zdarzenie } from '../../lib/pomiar'
import { pln } from './format'

/**
 * „Zestaw według Twoich preferencji” (plan „Rynek dla domu”, P4, 2026-10-24): po powrocie z Projektu 3D
 * (`?z3d=1` w asystencie) ktoś wybiera marki falowników i magazynów oraz full black, a my dobieramy zestaw do mocy
 * z modelu i zapisujemy wybrany wariant w teczce (część `sprzet`, `lib/teczka/sprzet.js`). Wykonawca widzi go
 * przy zleceniu. Bez konta teczka trzyma wybór w przeglądarce i przenosi go na konto po zalogowaniu (`useTeczka`).
 *
 * `zModelu`: dane z `lib/projekt3d/asystent3d.js` (`daneDlaAsystenta`): `kwp`, `magazynKwh`.
 */

export const MARKI_FALOWNIKOW = Object.keys(inverterManufacturers)
export const MARKI_MAGAZYNOW = Object.keys(magazineManufacturers)
const POKAZANE_WARIANTY = 3

/** Żądanie do silnika doboru z mocy modelu i preferencji. Null bez mocy. */
export const zapytanieWedlugPreferencji = (zModelu, preferencje) => {
  const kwp = Number(zModelu?.kwp) || null
  if (!kwp) return null
  const magazyn = Number(zModelu?.magazynKwh) || null
  const { marki_falownikow: falowniki = [], marki_magazynow: magazyny = [], full_black: fullBlack } = preferencje
  return {
    categories: ['panels', 'inverter', ...(magazyn ? ['battery'] : [])],
    budget_total_pln: null,
    installation_phase: null,
    panels: {
      target_power_kwp: kwp,
      filters: fullBlack ? { types_in: ['Full Black'] } : {},
      modes: fullBlack ? { types_in: 'required' } : {},
    },
    inverter: {
      // Ta sama reguła co w prostym doborze: falownik do mocy modułów, w pełnych kilowatach.
      power_kw_min: Math.max(1, Math.floor(kwp * 0.8)),
      power_kw_max: Math.ceil(kwp),
      filters: {
        ...(falowniki.length ? { manufacturer_in: falowniki } : {}),
        ...(magazyn ? { types_in: ['Hybrydowy'] } : {}),
      },
      modes: {
        ...(falowniki.length ? { manufacturer_in: 'required' } : {}),
        ...(magazyn ? { types_in: 'required' } : {}),
      },
    },
    battery: magazyn
      ? {
        capacity_kwh_min: Math.max(2, magazyn - 2), capacity_kwh_max: magazyn + 3, laczenie: true,
        filters: magazyny.length ? { manufacturer_in: magazyny } : {},
        modes: magazyny.length ? { manufacturer_in: 'required' } : {},
      }
      : { filters: {}, modes: {} },
    constraints: { require_available: true, allow_unknown_compatibility: false, single_seller_only: false },
  }
}

const GrupaMarek = ({ tytul, marki, wybrane, onPrzelacz }) => (
  <fieldset className="mt-4">
    <legend className="text-sm font-semibold text-gray-900">{tytul}</legend>
    <div className="mt-2 flex flex-wrap gap-2">
      {marki.map((m) => {
        const wl = wybrane.includes(m)
        return (
          <button key={m} type="button" aria-pressed={wl} onClick={() => onPrzelacz(m)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${
                    wl ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}>
            {wl && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
            {m}
          </button>
        )
      })}
    </div>
  </fieldset>
)

const ZestawWedlugPreferencji = ({ zModelu, zrodlo = 'projekt3d' }) => {
  const { t } = useTranslation('asystent')
  const { dostepna, zapiszCzesc } = useTeczka()
  const [preferencje, setPreferencje] = useState({ marki_falownikow: [], marki_magazynow: [], full_black: false })
  const [stan, setStan] = useState({ laduje: false, warianty: null, blad: null })
  const [wybrany, setWybrany] = useState(null)
  const [zapisany, setZapisany] = useState(null)
  const zMagazynem = Boolean(Number(zModelu?.magazynKwh))

  const przelacz = (pole) => (marka) => setPreferencje((p) => ({
    ...p, [pole]: p[pole].includes(marka) ? p[pole].filter((m) => m !== marka) : [...p[pole], marka],
  }))

  const dobierz = async () => {
    const zapytanie = zapytanieWedlugPreferencji(zModelu, preferencje)
    if (!zapytanie) return
    setStan({ laduje: true, warianty: null, blad: null })
    setWybrany(null)
    setZapisany(null)
    zdarzenie('dobor_preferencje', 'dobor')
    const res = await suggestKits(zapytanie)
    if (!res || res.error) {
      setStan({ laduje: false, warianty: null, blad: t('prosty.blad') })
      return
    }
    setStan({ laduje: false, warianty: (res.variants || []).slice(0, POKAZANE_WARIANTY), blad: null })
  }

  const zapisz = () => {
    const v = stan.warianty?.find((x) => x.variant === wybrany)
    if (!v) return
    zapiszCzesc('sprzet', sprzetZWariantu(v, { preferencje, zrodlo }))
    zdarzenie('dobor_preferencje', 'zapis')
    setZapisany(wybrany)
  }

  if (!Number(zModelu?.kwp)) return null

  return (
    <section className="mb-4 rounded-2xl border border-gray-200 bg-white px-4 py-4 md:px-5" aria-labelledby="zestaw-preferencji">
      <h2 id="zestaw-preferencji" className="text-base font-bold text-gray-900">{t('prosty.preferencje.tytul')}</h2>
      <p className="mt-1 text-sm text-gray-600">{t('prosty.preferencje.opis')}</p>

      <GrupaMarek tytul={t('prosty.preferencje.marki_falownikow')} marki={MARKI_FALOWNIKOW}
                  wybrane={preferencje.marki_falownikow} onPrzelacz={przelacz('marki_falownikow')} />
      {zMagazynem && (
        <GrupaMarek tytul={t('prosty.preferencje.marki_magazynow')} marki={MARKI_MAGAZYNOW}
                    wybrane={preferencje.marki_magazynow} onPrzelacz={przelacz('marki_magazynow')} />
      )}
      <label className="mt-4 flex items-center gap-2 text-sm text-gray-800">
        <input type="checkbox" checked={preferencje.full_black}
               onChange={(e) => setPreferencje((p) => ({ ...p, full_black: e.target.checked }))} />
        {t('prosty.preferencje.full_black')}
      </label>

      <button type="button" onClick={dobierz} disabled={stan.laduje}
              className="mt-4 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
        {t('prosty.preferencje.dobierz')}
      </button>

      {stan.blad && <p className="mt-3 text-sm text-red-700" role="alert">{stan.blad}</p>}
      {stan.warianty && stan.warianty.length === 0 && (
        <p className="mt-3 text-sm text-gray-700">{t('prosty.preferencje.brak')}</p>
      )}
      {stan.warianty?.length > 0 && (
        <div className="mt-4">
          <ul className="space-y-2">
            {stan.warianty.map((v) => (
              <li key={v.variant}>
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 ${
                  wybrany === v.variant ? 'border-blue-600 bg-blue-50/60' : 'border-gray-200'}`}>
                  <input type="radio" name="zestaw-preferencji" className="mt-1" checked={wybrany === v.variant}
                         onChange={() => { setWybrany(v.variant); setZapisany(null) }} />
                  <span className="min-w-0 flex-1 text-sm text-gray-800">
                    {(v.items || []).map((i) => `${i.quantity > 1 ? `${i.quantity} × ` : ''}${i.model_name}`).join(', ')}
                  </span>
                  {Number.isFinite(v.totals?.total_pln) && (
                    <span className="text-sm font-semibold tabular-nums text-gray-900">{pln(v.totals.total_pln)}</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" onClick={zapisz} disabled={!wybrany}
                    className="rounded-xl border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50">
              {t('prosty.preferencje.zapisz')}
            </button>
            {zapisany && (
              <p className="text-sm text-gray-700" role="status">
                {t('prosty.preferencje.zapisano')}{!dostepna ? ` ${t('prosty.preferencje.gosc')}` : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

export default ZestawWedlugPreferencji
